import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import FormWrapper from 'components/atoms/FormWrapper';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { addLegalEntityAttachment } from 'requests/attachment';
import { Grid, Box } from '@mui/material';
import { Resolver, useForm } from 'react-hook-form';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useDictionary } from 'models/dictionaryContext';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useQuery } from 'hooks/useQuery';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { validateAttachment } from 'utils/attachments';

const LegalEntityAddAttachmentPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const [attachment, setAttachment] = useState<any>(null);
  const { t } = useTranslations();
  const { attachmentDocumentTypeData, getAttachmentDocumentTypeData } = useDictionary();
  const { samePageAlert, setAlert, clearAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);

  const schema = yup.object().shape({
    attachmentDocumentTypeUniqueId: yup
      .string()
      .required(t('general.field.validation.required'))
      .trim(),
    attachmentName: yup.string().required(t('general.field.validation.required')).trim(),
  });

  const {
    control,
    handleSubmit: handleFormSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      attachmentName: '',
      attachmentDocumentTypeUniqueId: '',
    },
    resolver: yupResolver(schema) as unknown as Resolver,
  });

  const getOriginalQueryParams = () => {
    const state = location.state as { originalSearch?: string } | null;
    return state?.originalSearch || '';
  };

  useEffect(() => {
    getAttachmentDocumentTypeData();
    clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (data: any) => {
    if (!id) return;
    setIsLoading(true);
    const validation = validateAttachment(attachment, data.attachmentName);

    if (validation.hasError && validation.errorMessage) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t(validation.errorMessage),
        variant: EAlertVariants.error,
      });
      setIsLoading(false);
      return;
    }

    try {
      const submitData = {
        attachment,
        attachmentName: data.attachmentName,
        attachmentDocumentTypeUniqueId: data.attachmentDocumentTypeUniqueId,
      };

      const result = await addLegalEntityAttachment(id, submitData);
      if (result) {
        setAlert(ActionTypes.SET_ATTACHMENT_ALERT, {
          text: t('legalEntityDetails.attachments.add.success.text'),
        });
        const queryParams = getOriginalQueryParams();
        navigate(
          `${ERouteLinks.ViewLegalEntity.replace(':id', id)}${queryParams}&expand=attachments`,
        );
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response.data.message,
        variant: EAlertVariants.error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    const queryParams = getOriginalQueryParams();
    navigate(
      `${ERouteLinks.ViewLegalEntity.replace(':id', id as string)}${queryParams}&expand=attachments`,
    );
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.legalEntity.name'), route: ERouteLinks.LegalEntities },
          {
            label:
              type === ELegalEntityTypes.individual
                ? t('legalEntities.individual.accordion.label')
                : t('legalEntities.organization.accordion.label'),
            route: `${ERouteLinks.LegalEntities}?expand=${
              type === ELegalEntityTypes.individual
                ? LEAccordionNames.INDIVIDUAL
                : LEAccordionNames.ORGANISATION
            }`,
          },
          {
            label: t('legalEntityDetails.legalEntityDetails.text'),
            route: `${ERouteLinks.ViewLegalEntity.replace(
              ':id',
              id,
            )}?type=${type}&expand=attachments`,
          },
          t('legalEntityDetails.attachments.addNew.title'),
        ]}
      />
      <StyledPageTitle variant="h4">
        {t('legalEntityDetails.attachments.addNew.title')}
      </StyledPageTitle>

      {samePageAlert && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}

      <FormWrapper>
        <form onSubmit={handleFormSubmit(handleSubmit)}>
          <Grid container spacing="10px">
            <Grid size={{ xs: 12, md: 3 }}>
              <FormSearchSelect
                options={mapDictionaryToOptionProp(attachmentDocumentTypeData || [])}
                name="attachmentDocumentTypeUniqueId"
                label={t('legalEntityDetails.attachments.fields.attachmentType')}
                control={control}
                errors={errors}
                required
                withValidation
                disabled={!attachmentDocumentTypeData?.length}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormInput
                name="attachmentName"
                label={t('legalEntityDetails.attachments.fields.filename')}
                control={control}
                setValue={setValue}
                errors={errors}
                InputProps={{ disabled: !attachment?.name }}
                required
                withValidation
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Button
                id="legal-entity-attachment-browse-button"
                isUploadButton
                alignWithInputSX={{ xs: 12, md: 3 }}
                onClick={(files) => {
                  if (files && files[0]) {
                    setAttachment(files[0]);
                    setValue('attachmentName', files[0]?.name?.split('.')[0] || '');
                    clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
                  }
                }}
                fullWidth
                label={t('general.browse.button')}
                size={EButtonSizes.small}
                variant={EButtonVariants.outlined}
              />
            </Grid>
          </Grid>

          <Grid container columnSpacing={2} sx={{ mt: 3, rowGap: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Button
                id="legal-entity-attachment-save-button"
                type="submit"
                label={t('general.save.button')}
                variant={EButtonVariants.contained}
                disabled={!attachment || isLoading}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Button
                id="legal-entity-attachment-cancel-button"
                onClick={handleCancel}
                disabled={isLoading}
                label={t('general.cancel.button')}
                variant={EButtonVariants.outlined}
                fullWidth
              />
            </Grid>
          </Grid>
        </form>
      </FormWrapper>
    </>
  );
};

export { LegalEntityAddAttachmentPage };
